import { Data } from "../../../../../ts/models/data";
import { Context } from "../../../../../ts/models/context";
import { GeoSearchProviderFactory, GeoSearchSelection } from "../../../../../ts/services/geo-search/geo-search-provider-factory";
import { getGeoServiceApiKeys } from "../../../../services/geo-service-api-keys";
import { IMapInstance, LatLng } from "../../../../services/maps/map-provider.interface";
import { LocalizationService } from "../../../../services/localization-service";
import { AddressSuggestBox } from "../../../shared/AddressSuggestBox/address-suggest-box";

export class AddressSearchBox {
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _containerElement: HTMLElement;
    private readonly _localizationService: LocalizationService;

    private _geoSearch: GeoSearchSelection | null = null;
    private _addressSuggestBox: AddressSuggestBox | null = null;
    private _mapInstance: IMapInstance | null = null;

    constructor(
        containerElement: HTMLElement,
        data: Data,
        context: Context,
        localizationService: LocalizationService,
    ) {
        this._containerElement = containerElement;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
    }

    public bindAutoComplete(
        mapInstance: IMapInstance,
        onPlaceSelected: (coords: LatLng) => void,
    ): void {
        const chain = this._getGeoSearch().chain;
        if (!chain?.hasSuggestProvider)
            return;

        const input = this._containerElement.querySelector<HTMLInputElement>("#txt_store_selector_search");
        if (!input)
            return;

        this._mapInstance = mapInstance;

        this._addressSuggestBox?.dispose();
        this._addressSuggestBox = new AddressSuggestBox({
            input,
            chain,
            mode: "locality",
            getBiasLocation: () => this._getMapCenter(),
            getCountryCodes: () => (this._data.getBrandProperty("Countries") ?? []).map(country => country.IsoCode),
            getAnnouncement: () => this._localizationService.names("AutoSuggestionsAvailable"),
            onAddressSelected: ({ coords }) => onPlaceSelected(coords)
        });
    }

    public async getCoordsFromZipCodeAsync(
        zipCode: string,
        countryId: string,
    ): Promise<LatLng | null> {
        const countryName =
            this._data.getBrandProperty("Countries").find((c) => c.CountryID === Number(countryId))?.Name ??
            "United States of America";

        const addressQuery =
            zipCode && zipCode.trim().length > 0 ? `${zipCode}, ${countryName}` : countryName;

        const coords = await this._getGeoSearch().chain?.geocodeAsync(addressQuery).catch(() => null);
        return coords ?? null;
    }

    public dispose(): void {
        this._addressSuggestBox?.dispose();
        this._addressSuggestBox = null;
    }

    private _getMapCenter(): LatLng | null {
        const bounds = this._mapInstance?.getBoundsRect?.();
        if (!bounds)
            return null;

        return {
            lat: (bounds.north + bounds.south) / 2,
            lng: (bounds.east + bounds.west) / 2
        };
    }

    /**
     * Setting 275 arrives with the brand data, after the modal's init() has already constructed
     * this component, so the providers are resolved on first use instead of in the constructor.
     */
    private _getGeoSearch(): GeoSearchSelection {
        this._geoSearch ??= GeoSearchProviderFactory.createSelection(
            this._data.getBrandProperty("Settings").LOCSET,
            this._data.getBrandProperty("Settings").OOS.autocompleteProvider,
            getGeoServiceApiKeys(this._data, this._context)
        );

        return this._geoSearch;
    }
}
